import { usePage, router } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

const navItems = [
  { id: 'surveys', label: 'Surveys', path: '/surveys', icon: 'lucide--clipboard-list', exact: false },
  { id: 'templates', label: 'Templates', path: '/survey_templates', icon: 'lucide--file-text', exact: false },
]

export default function AdminTopNav() {
  const { current_user, current_membership, current_account, accounts } =
    usePage<{ props: SharedProps }>().props as unknown as SharedProps
  const ap = useAccountPath()
  const currentPath = window.location.pathname
  const showTeam = current_membership?.admin ?? false
  const canSwitch = current_account != null && (accounts?.length ?? 0) > 1

  function isActive(path: string, exact: boolean) {
    const full = ap(path)
    return exact ? currentPath === full : currentPath.startsWith(full)
  }

  const settingsActive = currentPath.startsWith(ap('/settings')) || currentPath === ap('/facilitators')

  function handleLogout() {
    router.delete('/logout')
  }

  // Settings sub-items — moved off the account menu into the Settings dropdown.
  const settingsItems = [
    { label: 'Account Settings', path: '/settings', icon: 'lucide--sliders-horizontal', show: true },
    { label: 'Facilitators', path: '/facilitators', icon: 'lucide--users', show: true },
    { label: 'Team', path: '/settings/team', icon: 'lucide--user-cog', show: showTeam },
    { label: 'Billing', path: '/settings/billing', icon: 'lucide--credit-card', show: showTeam },
    { label: 'API Keys', path: '/settings/api-keys', icon: 'lucide--key', show: showTeam },
  ].filter((item) => item.show)

  const userInitial = current_account?.name?.charAt(0).toUpperCase()
    || current_user?.consultancy_name?.charAt(0).toUpperCase()
    || current_user?.email_address?.charAt(0).toUpperCase()
    || '?'

  const accountLabel = current_account?.name
    || current_user?.consultancy_name
    || current_user?.email_address?.split('@')[0]
    || 'Account'

  return (
    <header className="account-topnav">
      <div className="account-topnav-inner">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <a href={ap('/surveys')} className="flex items-center gap-2 font-semibold text-lg">
            {current_user?.logo_url ? (
              <img
                src={current_user.logo_url}
                alt=""
                className="h-8 max-w-[8rem] object-contain"
              />
            ) : (
              <img src="/logo-white.svg" alt="" className="size-7" />
            )}
            <span className="hidden sm:inline truncate max-w-32">ImpactCheck</span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={ap(item.path)}
                className={`nav-item ${isActive(item.path, item.exact) ? 'active' : ''}`}
              >
                <span className={`iconify ${item.icon} size-4`} />
                <span>{item.label}</span>
              </a>
            ))}

            {/* Settings dropdown — holds the account/team/billing/api-key pages */}
            <div className="dropdown">
              <div
                tabIndex={0}
                role="button"
                className={`nav-item ${settingsActive ? 'active' : ''}`}
              >
                <span className="iconify lucide--settings size-4" />
                <span>Settings</span>
                <span className="iconify lucide--chevron-down size-3.5 opacity-70" />
              </div>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 text-base-content rounded-box w-52 p-2 shadow-lg border border-base-300 z-50"
              >
                {settingsItems.map((item) => (
                  <li key={item.path}>
                    <a href={ap(item.path)}>
                      <span className={`iconify ${item.icon} size-4`} />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {/* Account menu — the current account + switcher + logout */}
          <div className="dropdown dropdown-end">
            <div
              tabIndex={0}
              role="button"
              className="btn btn-ghost btn-sm gap-2 text-primary-content hover:bg-white/10"
            >
              <div className="avatar avatar-placeholder">
                <div className="bg-white/20 text-primary-content rounded-full w-6">
                  <span className="text-xs">{userInitial}</span>
                </div>
              </div>
              <span className="hidden sm:inline text-sm truncate max-w-32">{accountLabel}</span>
              <span className="iconify lucide--chevron-down size-4" />
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 text-base-content rounded-box w-64 p-2 shadow-lg border border-base-300 z-50"
            >
              {canSwitch && (
                <>
                  <li className="menu-title text-xs">Switch account</li>
                  {accounts!.map((account) => (
                    <li key={account.slug}>
                      <a
                        href={account.url ?? `/${account.slug}/surveys`}
                        className={account.slug === current_account!.slug ? 'active' : ''}
                      >
                        {account.logo_url ? (
                          <img src={account.logo_url} alt="" className="size-5 object-contain rounded" />
                        ) : (
                          <span className="iconify lucide--building-2 size-4 opacity-60" />
                        )}
                        <span className="flex-1 truncate">{account.name}</span>
                        {account.slug === current_account!.slug && (
                          <span className="iconify lucide--check size-4 text-primary" />
                        )}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a href="/accounts">
                      <span className="iconify lucide--layout-grid size-4" />
                      All accounts
                    </a>
                  </li>
                  <div className="divider my-1" />
                </>
              )}
              <li>
                <button onClick={handleLogout}>
                  <span className="iconify lucide--log-out size-4" />
                  Logout
                </button>
              </li>
            </ul>
          </div>

          {/* Mobile menu toggle */}
          <label
            htmlFor="account-mobile-menu"
            className="btn btn-ghost btn-sm btn-square lg:hidden text-primary-content hover:bg-white/10"
          >
            <span className="iconify lucide--menu size-5" />
          </label>
        </div>
      </div>

      {/* Mobile drawer */}
      <input type="checkbox" id="account-mobile-menu" className="hidden" />
      <div className="account-mobile-menu lg:hidden">
        <label htmlFor="account-mobile-menu" className="account-mobile-backdrop" />
        <div className="account-mobile-drawer">
          <div className="flex items-center justify-between p-4 border-b border-base-200">
            <span className="font-semibold truncate">{accountLabel}</span>
            <label htmlFor="account-mobile-menu" className="btn btn-ghost btn-sm btn-circle">
              <span className="iconify lucide--x size-5" />
            </label>
          </div>

          <nav className="p-2 flex-1 overflow-y-auto">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={ap(item.path)}
                className={`mobile-nav-item ${isActive(item.path, item.exact) ? 'active' : ''}`}
              >
                <span className={`iconify ${item.icon} size-4`} />
                <span>{item.label}</span>
              </a>
            ))}

            <p className="px-3 pt-3 pb-1 text-xs font-semibold text-base-content/50">Settings</p>
            {settingsItems.map((item) => (
              <a
                key={item.path}
                href={ap(item.path)}
                className={`mobile-nav-item ${currentPath === ap(item.path) ? 'active' : ''}`}
              >
                <span className={`iconify ${item.icon} size-4`} />
                <span>{item.label}</span>
              </a>
            ))}

            {canSwitch && (
              <>
                <p className="px-3 pt-3 pb-1 text-xs font-semibold text-base-content/50">Switch account</p>
                {accounts!.map((account) => (
                  <a
                    key={account.slug}
                    href={account.url ?? `/${account.slug}/surveys`}
                    className={`mobile-nav-item ${account.slug === current_account!.slug ? 'active' : ''}`}
                  >
                    <span className="iconify lucide--building-2 size-4" />
                    <span className="flex-1 truncate">{account.name}</span>
                    {account.slug === current_account!.slug && (
                      <span className="iconify lucide--check size-4 text-primary" />
                    )}
                  </a>
                ))}
                <a href="/accounts" className="mobile-nav-item">
                  <span className="iconify lucide--layout-grid size-4" />
                  <span>All accounts</span>
                </a>
              </>
            )}
          </nav>

          <div className="border-t border-base-200 p-2">
            <button
              onClick={handleLogout}
              className="mobile-nav-item w-full text-error"
            >
              <span className="iconify lucide--log-out size-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}
